import React from 'react';
import { Link } from 'react-router-dom';
import './About.css';
import useReveal from '../hooks/useReveal';
import CountUp from '../components/CountUp';
import TokenRevealText from '../components/TokenRevealText';

const About = () => {
  useReveal();
  const teamStats = [
    { value: '40+', label: 'Professionals' },
    { value: '10+', label: 'Engineers' },
    { value: '20+', label: 'Machines' },
  ];

  const teamDepts = [
    { dept: 'Product Design' },
    { dept: 'Operations' },
    { dept: 'Marketing' },
    { dept: 'Tool Room' },
    { dept: 'Production' },
    { dept: 'Quality Control' },
  ];

  const caseStudies = [
    {
      title: 'Industrial Enclosure Redesign',
      problem: 'Legacy enclosure cracked under thermal cycling and failed IP rating in field use.',
      solution: 'Re-engineered wall thickness, gasket geometry, and tooling to improve durability and sealing.',
      result: 'Field failures reduced by 62% with 98% IP compliance across production batches.',
    },
    {
      title: 'Precision Tooling for High-Volume Parts',
      problem: 'Dimensional drift caused rework and low yield in high-volume runs.',
      solution: 'Upgraded mold cooling layout and introduced in-process inspection checkpoints.',
      result: 'Yield increased to 96% and cycle time dropped by 18% without tolerance drift.',
    },
    {
      title: 'Device Housing Integration',
      problem: 'Multiple vendors led to long lead times and assembly misalignment.',
      solution: 'Consolidated design, tooling, and manufacturing into one integrated flow.',
      result: 'Lead time cut by 35% with a 22% improvement in assembly fit consistency.',
    },
  ];

  return (
    <main className="about-page page-enter">
      {/* Page Hero */}
      <section className="page-hero">
        <div className="container reveal">
          <p className="page-eyebrow"><TokenRevealText text="Who We Are" speed="medium" /></p>
          <h1><TokenRevealText text="About Us" speed="medium" /></h1>
          <nav className="breadcrumb">
            <Link to="/"><TokenRevealText text="Home" speed="medium" /></Link>
            <span> &rsaquo; </span>
            <span><TokenRevealText text="About Us" speed="medium" /></span>
          </nav>
        </div>
      </section>

      {/* ===== COMPANY OVERVIEW ===== */}
      <section id="overview" className="section about-overview-section">
        <div className="container grid-2">
          <div className="media-card reveal reveal-left" style={{ height: '400px' }}>
            <img
              src="/images/AR7A3793.jpg"
              alt="C2P Office Building"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/images/C2P.jpg';
              }}
            />
          </div>
          <div className="reveal reveal-right">
            <p className="about-eyebrow"><TokenRevealText text="Company Overview" speed="medium" /></p>
            <h2 className="about-title"><TokenRevealText text="C2P Engineering Solutions Pvt. Ltd." speed="medium" /></h2>
            <div className="about-divider" />
            <p className="body-text">
              <TokenRevealText text="Incorporated in September 2003, C2P is a" speed="medium" />{' '}
              <strong><TokenRevealText text="Product Design & Development" speed="medium" /></strong>{' '}
              <TokenRevealText text="company based at Hyderabad, India." speed="medium" />
            </p>
            <p className="body-text">
              <TokenRevealText text="A" speed="medium" />{' '}
              <strong><TokenRevealText text="Group Company" speed="medium" /></strong>{' '}
              <TokenRevealText text="of Analogics Tech India Ltd., C2P is an" speed="medium" />{' '}
              <strong><TokenRevealText text="End-to-end solution provider" speed="medium" /></strong>{', '}
              <TokenRevealText text="specializing in high precision" speed="medium" />{' '}
              <strong><TokenRevealText text="Tooling" speed="medium" /></strong>{' '}
              <TokenRevealText text="and" speed="medium" />{' '}
              <strong><TokenRevealText text="Manufacturing" speed="medium" /></strong>{'.'}
            </p>
            <p className="body-text">
              <TokenRevealText
                text="Our integrated approach covers Concept, Product Design, Prototype Development, Mold Development, and Production – all under one roof, ensuring faster cycles and superior product quality."
                speed="medium"
              />
            </p>
            {/* Key Highlights */}
            <div className="kh-grid">
              {[
                { label: 'Established', val: '2003' },
                { label: 'Location', val: 'Hyderabad' },
                { label: 'Professionals', val: '40+' },
                { label: 'Facility', val: '20,933 sq.ft.' },
              ].map((h, i) => (
                <div key={h.label} className={`kh-box reveal reveal-scale reveal-d${i + 1}`}>
                  <span className="kh-val"><TokenRevealText text={h.val} speed="medium" /></span>
                  <span className="kh-label"><TokenRevealText text={h.label} speed="medium" /></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== MISSION & VISION ===== */}
      <section id="mission" className="section section-alt about-purpose-section">
        <div className="container">
          <div className="reveal" style={{ textAlign: 'center', marginBottom: '48px' }}>
            <p className="about-eyebrow" style={{ textAlign: 'center' }}>
              <TokenRevealText text="Our Purpose" speed="medium" />
            </p>
            <h2 className="about-title" style={{ textAlign: 'center' }}>
              <TokenRevealText text="Mission & Vision" speed="medium" />
            </h2>
            <div className="about-divider" style={{ margin: '12px auto 0' }} />
          </div>
          <div className="mv-row">
            <div className="mv-item reveal reveal-left reveal-d1">
              <div className="mv-item-icon">M</div>
              <div>
                <h4><TokenRevealText text="Our Mission" speed="medium" /></h4>
                <p>
                  <TokenRevealText
                    text="Delivering Innovation & Quality – providing complete engineering solutions that help customers transform ideas into reliable, high-precision products."
                    speed="medium"
                  />
                </p>
              </div>
            </div>
            <div className="mv-item reveal reveal-right reveal-d2">
              <div className="mv-item-icon">V</div>
              <div>
                <h4><TokenRevealText text="Our Vision" speed="medium" /></h4>
                <p>
                  <TokenRevealText
                    text="To become a leading engineering partner – a trusted national provider of manufacturing solutions through innovation, quality, and customer focus."
                    speed="medium"
                  />
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TEAM ===== */}
      <section id="team" className="section about-team-section">
        <div className="container">
          <div className="reveal" style={{ textAlign: 'center', marginBottom: '40px' }}>
            <p className="about-eyebrow" style={{ textAlign: 'center' }}>
              <TokenRevealText text="Our People" speed="medium" />
            </p>
            <h2 className="about-title" style={{ textAlign: 'center' }}>
              <TokenRevealText text="Our Team" speed="medium" />
            </h2>
            <div className="about-divider" style={{ margin: '12px auto 0' }} />
          </div>
          <div className="team-layout">
            <div className="team-images-col reveal reveal-left">
              <div className="team-img-main media-card" style={{ height: '260px' }}>
                <img
                  src="/images/C2P-staff.jpg"
                  alt="Team"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/images/C2P-staff.jpg';
                  }}
                />
              </div>
              <div className="team-stats-row">
                {teamStats.map((s, i) => (
                  <div key={s.label} className={`team-stat reveal reveal-scale reveal-d${i + 1}`}>
                    <span className="ts-value"><CountUp value={s.value} /></span>
                    <span className="ts-label"><TokenRevealText text={s.label} speed="medium" /></span>
                  </div>
                ))}
              </div>
            </div>
            <div className="team-info-col reveal reveal-right">
              <p className="body-text">
                <TokenRevealText text="Our team consists of" speed="medium" />{' '}
                <strong><TokenRevealText text="40+ skilled full-time professionals" speed="medium" /></strong>{' '}
                <TokenRevealText
                  text="across product design, operations, marketing, tool room, production, and quality control. This multidisciplinary expertise allows us to deliver integrated solutions from concept to production under one roof."
                  speed="medium"
                />
              </p>
              <div className="team-depts">
                {teamDepts.map((t, i) => (
                  <div key={t.dept} className={`team-dept-chip reveal reveal-scale reveal-d${(i % 3) + 1}`}>
                    <span><TokenRevealText text={t.dept} speed="medium" /></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CASE STUDIES ===== */}
      <section className="section section-alt about-case-studies">
        <div className="container">
          <div className="reveal" style={{ textAlign: 'center', marginBottom: '40px' }}>
            <p className="about-eyebrow" style={{ textAlign: 'center' }}>
              <TokenRevealText text="Case Studies" speed="medium" />
            </p>
            <h2 className="about-title" style={{ textAlign: 'center' }}>
              <TokenRevealText text="Problem – Solution – Result" speed="medium" />
            </h2>
            <div className="about-divider" style={{ margin: '12px auto 0' }} />
          </div>
          <div className="case-grid">
            {caseStudies.map((cs, i) => (
              <div key={cs.title} className={`case-card reveal reveal-scale reveal-d${i + 1}`}>
                <h3><TokenRevealText text={cs.title} speed="medium" /></h3>
                <div className="case-row">
                  <span className="case-label"><TokenRevealText text="Problem" speed="medium" /></span>
                  <p><TokenRevealText text={cs.problem} speed="medium" /></p>
                </div>
                <div className="case-row">
                  <span className="case-label"><TokenRevealText text="Solution" speed="medium" /></span>
                  <p><TokenRevealText text={cs.solution} speed="medium" /></p>
                </div>
                <div className="case-row">
                  <span className="case-label"><TokenRevealText text="Result" speed="medium" /></span>
                  <p><TokenRevealText text={cs.result} speed="medium" /></p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="about-cta">
        <div className="container about-cta-inner">
          <div className="reveal reveal-left">
            <h2><TokenRevealText text="Ready to Work With Us?" speed="medium" /></h2>
            <p><TokenRevealText text="Get an Estimate for Your Project Today!" speed="medium" /></p>
          </div>
          <Link to="/contact" className="btn-primary reveal reveal-right">Let's Connect</Link>
        </div>
      </section>
    </main>
  );
};

export default About;